import { useNavigate, useParams } from "react-router-dom";
import { useBoards } from "../../utils/reactQuery/boards";
import { useEffect } from "react";
import { isNumericBoardId, getBoardKeyFromNumericId } from "../../utils/boardUtils";
import { useCustomSearchParams } from "../../hooks/useCustomSearchParams";

/**
 * BoardUrlController - A controller component that handles backward compatibility for board URLs
 * 
 * This controller detects if a URL contains a numeric board_id (old format)
 * and redirects to the new URL format using the board key.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render when redirection isn't needed
 * @returns {React.ReactNode} - Either children or null during redirection
 */
const BoardUrlController = ({ children }) => {
  const { id } = useParams();
  const [cardId] = useCustomSearchParams('card_id');
  const navigate = useNavigate();
  const { isLoading, boards, isError } = useBoards();
  
  useEffect(() => {
    // Don't do anything if still loading the boards or if there's an error
    if (isLoading || isError || !boards) return;
    
    const responseData = boards?.data?.data?.responseData;
    
    // Check if the ID is in the old numeric format
    if (isNumericBoardId(id)) {
      console.log("Detected legacy board ID format, redirecting to new URL format");
      
      // Find the corresponding board key for the numeric ID
      const boardKey = getBoardKeyFromNumericId(responseData, id);
      
      if (boardKey) {
        // Construct the new URL with the board key
        const newPath = cardId
          ? `/board/${boardKey}/${boardKey}-${cardId}`
          : `/board/${boardKey}`;
        
        // Redirect to the new URL
        navigate(newPath, { replace: true });
      } else {
        console.error(`Could not find board with ID: ${id}`);
      }
    }
  }, [id, cardId, isLoading, boards, navigate, isError]);
  
  // Render children if not redirecting
  return children;
};

export default BoardUrlController; 